// src/emailQueue.ts
//
// Offline outbox for proposal emails. The send sheet drops a send in here when
// the device is offline (or the send fails with a network-class error) and
// tells the user it will go out next time they're online.
//
// That promise used to be impossible to keep: a queued item stored no replyTo,
// and send-proposal-email hard-rejects a body without one (400 "Missing
// required fields: to, subject, html, replyTo"). So every queued proposal
// failed on every flush, forever — and the only reporting was a debug log that
// fires exclusively when sent > 0, so nothing ever surfaced. The salesman
// believed the proposal went out; the customer never received it.
//
// The queue therefore now carries everything the edge function needs, the
// client is injected so this is testable against the real validation shape,
// and a flush reports failures instead of swallowing them.

import { supabase as defaultSupabase } from "./supabaseClient";
import type { SupabaseClient } from "@supabase/supabase-js";

export type QueuedEmail = {
  id: string; // unique client id
  createdAt: number;
  proposalId: string;
  to: string;
  subject: string;
  body: string;
  /** Required by the edge function. Absent on items queued by builds
   *  before this was fixed — flushEmailQueue's fallback rescues those. */
  replyTo?: string;
  /** "Send me a copy" — carried so the queued send matches the live one. */
  cc?: string[];
  /** Plain-text alternative part. */
  text?: string;
  // optional, but nice to have
  fromName?: string;
  attemptCount?: number;
  lastError?: string;
};

const KEY = "du_email_queue_v1";
const uuid = () => {
  const c: any = typeof crypto !== "undefined" ? crypto : null;
  return typeof c?.randomUUID === "function"
    ? c.randomUUID()
    : `${Date.now()}-${Math.random()}`;
};

function readQueue(): QueuedEmail[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedEmail[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Quota or blocked storage. Nothing useful to do here — throwing would
    // lose the in-memory result of a flush that may have sent successfully.
  }
}

export function enqueueEmail(item: Omit<QueuedEmail, "id" | "createdAt">) {
  const q = readQueue();
  q.push({
    id: uuid(),
    createdAt: Date.now(),
    attemptCount: 0,
    ...item,
  });
  writeQueue(q);
}

/** How many queued items are waiting, for callers that want to surface it. */
export function queuedEmailCount(): number {
  return readQueue().length;
}

export type FlushResult = {
  sent: number;
  /** Items that were attempted and did not go out. They stay queued. */
  failed: number;
  remaining: number;
  /** Last error per still-queued item, so a caller can actually tell the
   *  user something instead of failing silently. */
  errors: string[];
};

export async function flushEmailQueue(
  client: SupabaseClient = defaultSupabase,
  opts: { fallbackReplyTo?: string } = {}
): Promise<FlushResult> {
  const q = readQueue();
  if (!q.length) return { sent: 0, failed: 0, remaining: 0, errors: [] };

  let sent = 0;
  let failed = 0;
  const remaining: QueuedEmail[] = [];

  for (let i = 0; i < q.length; i++) {
    const item = q[i];

    // Still offline — keep this item and everything after it, untouched.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      remaining.push(...q.slice(i));
      break;
    }

    // Items queued before replyTo was carried have none. The caller passes the
    // signed-in user's address so those still go out rather than 400 forever.
    const replyTo = (item.replyTo || opts.fallbackReplyTo || "").trim();

    try {
      const { error } = await client.functions.invoke("send-proposal-email", {
        body: {
          to: item.to,
          subject: item.subject,
          html: item.body,
          proposalId: item.proposalId,
          replyTo,
          cc: item.cc,
          text: item.text,
        },
      });

      if (error) throw error;

      sent += 1;
    } catch (err: any) {
      failed += 1;
      remaining.push({
        ...item,
        attemptCount: (item.attemptCount || 0) + 1,
        lastError: String(err?.message || err || "Send failed"),
      });
    }
  }

  writeQueue(remaining);
  return {
    sent,
    failed,
    remaining: remaining.length,
    errors: remaining.map((r) => r.lastError || "").filter(Boolean),
  };
}
